import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Circle, Share2, Download, Plus, Users, ChevronDown } from "lucide-react";
import { exportDocument } from "@/lib/exportDocument";
import { FollowUpChat } from "@/components/rexxpons/FollowUpChat";
import { MediaGallery } from "@/components/rexxpons/MediaGallery";
import { TranslateControl } from "@/components/rexxpons/TranslateControl";
import { SubmitReferenceModal } from "@/components/rexxpons/SubmitReferenceModal";
import { UnifiedCognitivePanel } from "@/components/rexxpons/UnifiedCognitivePanel";
import { getApprovedReferences } from "@/lib/rexxponsApi";

const SECTIONS = [
  { id: "in-brief", label: "In Brief" },
  { id: "analysis", label: "The Analysis" },
  { id: "key-facts", label: "Key Facts" },
  { id: "consensus", label: "Consensus" },
  { id: "sources", label: "Verified Sources" },
];

const CONSENSUS_LABEL = {
  agreement: "Broad Agreement",
  debate: "Active Debate",
  emerging: "Emerging View",
};

function renderInline(text, keyPrefix) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={`${keyPrefix}-b-${i}`}>{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={`${keyPrefix}-t-${i}`}>{p}</React.Fragment>;
  });
}

const SectionHeading = ({ index, children }) => (
  <div className="flex items-baseline gap-4 mb-8">
    <span className="font-sans text-xs text-primary tracking-[0.2em]">0{index}</span>
    <h2 className="font-serif font-medium text-2xl sm:text-3xl tracking-tight">{children}</h2>
  </div>
);

const Analysis = ({ text }) => {
  const paras = (text || "").split(/\n{2,}|\n/).map((s) => s.trim()).filter(Boolean);
  return (
    <div className="prose-lonero font-serif text-lg sm:text-xl leading-relaxed text-foreground/90">
      {paras.map((p, i) => (
        <p key={`${i}-${p.slice(0, 24)}`}>{renderInline(p, `p${i}`)}</p>
      ))}
    </div>
  );
};

const Sidebar = ({ active, onBack }) => (
  <aside className="hidden lg:block lg:col-span-3">
    <div className="sticky top-28">
      <button
        data-testid="back-btn"
        onClick={onBack}
        className="flex items-center gap-2 font-sans text-xs tracking-[0.18em] uppercase text-muted-foreground hover:text-foreground transition-colors mb-10"
      >
        <ArrowLeft strokeWidth={1.5} className="w-3.5 h-3.5" /> New Query
      </button>
      <nav className="space-y-3">
        {SECTIONS.map((s, i) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            data-testid={`nav-${s.id}`}
            className={`block font-sans text-sm tracking-wide transition-colors ${
              active === s.id ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <span className="text-xs mr-2 opacity-60">0{i + 1}</span>
            {s.label}
          </a>
        ))}
      </nav>
    </div>
  </aside>
);

const ExportControl = ({ onExport, hasUnified }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  if (!hasUnified) {
    return (
      <button
        data-testid="insight-export-btn"
        onClick={() => onExport("en")}
        aria-label="Export as PDF"
        className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-primary transition-colors border border-border hover:border-primary/40 rounded-full px-3 py-1.5"
      >
        <Download strokeWidth={1.5} className="w-3.5 h-3.5" /> Export
      </button>
    );
  }
  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="insight-export-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label="Export report"
        className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-primary transition-colors border border-border hover:border-primary/40 rounded-full px-3 py-1.5"
      >
        <Download strokeWidth={1.5} className="w-3.5 h-3.5" /> Export
        <ChevronDown strokeWidth={1.6} className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid="export-menu" className="absolute right-0 mt-2 w-44 bg-card border border-border rounded-xl shadow-2xl p-1.5 z-50">
          <p className="px-2.5 py-1.5 text-[10px] tracking-[0.16em] uppercase text-muted-foreground">Report language</p>
          <button data-testid="export-en" onClick={() => { setOpen(false); onExport("en"); }} className="w-full text-left text-sm px-2.5 py-2 rounded-lg text-foreground hover:bg-secondary transition-colors">English</button>
          <button data-testid="export-it" onClick={() => { setOpen(false); onExport("it"); }} className="w-full text-left text-sm px-2.5 py-2 rounded-lg text-foreground hover:bg-secondary transition-colors">Italiano</button>
        </div>
      )}
    </div>
  );
};

const DocHeader = ({ title, onBack, onShare, onExport, hasUnified }) => (
  <>
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p className="font-sans text-xs tracking-[0.25em] uppercase text-muted-foreground">
        Rexxpons · RexxSearch Insight
      </p>
      <div className="flex items-center gap-2 shrink-0">
        <ExportControl onExport={onExport} hasUnified={hasUnified} />
        {onShare && (
          <button
            data-testid="insight-share-btn"
            onClick={onShare}
            aria-label="Share insight"
            className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-primary transition-colors border border-border hover:border-primary/40 rounded-full px-3 py-1.5"
          >
            <Share2 strokeWidth={1.5} className="w-3.5 h-3.5" /> Share
          </button>
        )}
      </div>
    </div>
    <button
      data-testid="mobile-back-btn"
      onClick={onBack}
      className="lg:hidden flex items-center gap-2 font-sans text-xs tracking-[0.18em] uppercase text-muted-foreground mb-8"
    >
      <ArrowLeft strokeWidth={1.5} className="w-3.5 h-3.5" /> New Query
    </button>
    <h1
      data-testid="doc-title"
      className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mb-14"
    >
      {title}
    </h1>
  </>
);

const InBriefSection = ({ text }) => (
  <section id="in-brief" className="scroll-mt-28 pb-20">
    <SectionHeading index={1}>In Brief</SectionHeading>
    <p
      data-testid="doc-in-brief"
      className="font-serif text-2xl sm:text-3xl font-light leading-snug text-foreground"
    >
      {text}
    </p>
  </section>
);

const KeyFactsSection = ({ facts }) => (
  <section id="key-facts" className="scroll-mt-28 py-20">
    <SectionHeading index={3}>Key Facts</SectionHeading>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {facts.map((f, i) => (
        <div
          key={`${i}-${f.slice(0, 24)}`}
          data-testid="key-fact"
          className="bg-secondary/60 border border-border rounded-md p-5"
        >
          <span className="font-sans text-xs text-primary tracking-[0.2em] block mb-2">
            {String(i + 1).padStart(2, "0")}
          </span>
          <p className="font-sans text-sm sm:text-base leading-relaxed text-foreground/90">{f}</p>
        </div>
      ))}
    </div>
  </section>
);

const ConsensusSection = ({ consensus }) => (
  <section id="consensus" className="scroll-mt-28 py-20">
    <SectionHeading index={4}>Consensus &amp; Perspectives</SectionHeading>
    <div className="flex items-center gap-2.5 mb-6">
      <Circle strokeWidth={0} className="w-2 h-2 fill-primary text-primary" />
      <span className="font-sans text-xs tracking-[0.18em] uppercase text-primary">
        {CONSENSUS_LABEL[consensus.state] || "Perspectives"}
      </span>
    </div>
    <p className="font-serif text-xl sm:text-2xl font-light leading-snug text-foreground/90 mb-10">
      {consensus.summary}
    </p>
    <div className="space-y-6">
      {consensus.perspectives.map((p, i) => (
        <div
          key={`${i}-${p.label}`}
          data-testid="perspective"
          className="border-l-2 border-primary/40 pl-5 py-1"
        >
          <p className="font-sans text-xs tracking-[0.14em] uppercase text-muted-foreground mb-1.5">
            {p.label}
          </p>
          <p className="font-serif text-lg leading-relaxed text-foreground/90">{p.view}</p>
        </div>
      ))}
    </div>
  </section>
);

const SourcesSection = ({ sources }) => (
  <section id="sources" className="scroll-mt-28 pt-20">
    <SectionHeading index={5}>Verified Sources</SectionHeading>
    <ul className="divide-y divide-border border-t border-b border-border">
      {sources.map((s, i) => (
        <li key={`${i}-${s.url}`} data-testid="source-item">
          <a
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-between gap-4 py-4 hover:bg-secondary/40 -mx-2 px-2 transition-colors"
          >
            <div className="min-w-0">
              <p className="font-sans text-sm sm:text-base text-foreground truncate">{s.name}</p>
              <p className="font-sans text-xs text-muted-foreground truncate">{s.url}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="font-sans text-[10px] tracking-[0.14em] uppercase text-muted-foreground border border-border rounded-full px-2.5 py-1">
                {s.kind}
              </span>
              <ExternalLink
                strokeWidth={1.5}
                className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors"
              />
            </div>
          </a>
        </li>
      ))}
    </ul>
    <p className="font-sans text-xs text-muted-foreground/70 mt-8 leading-relaxed">
      Synthesized by the Rexxpons RexxSearch engine from the sources above. Rexxpons maps disagreement rather than guessing — always verify critical decisions against primary sources.
    </p>
  </section>
);

function useActiveSection(doc) {
  const [active, setActive] = useState("in-brief");
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [doc]);
  return active;
}

const CommunityRefs = ({ refs }) => {
  if (!refs || refs.length === 0) return null;
  return (
    <div className="mt-10" data-testid="community-refs">
      <div className="flex items-center gap-2.5 mb-4">
        <Users strokeWidth={1.5} className="w-4 h-4 text-primary" />
        <span className="font-sans text-xs tracking-[0.18em] uppercase text-primary">Community References</span>
      </div>
      <ul className="divide-y divide-border border-t border-b border-border">
        {refs.map((r, i) => (
          <li key={`${i}-${r.url}`} data-testid="community-ref-item">
            <a href={r.url} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between gap-4 py-4 hover:bg-secondary/40 -mx-2 px-2 transition-colors">
              <div className="min-w-0">
                <p className="font-sans text-sm sm:text-base text-foreground truncate">{r.title}</p>
                <p className="font-sans text-xs text-muted-foreground truncate">{r.note || r.url}</p>
              </div>
              <ExternalLink strokeWidth={1.5} className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const InsightDocument = ({ doc, onBack, onShare }) => {
  const [current, setCurrent] = useState(doc);
  const [approved, setApproved] = useState([]);
  const [refOpen, setRefOpen] = useState(false);
  useEffect(() => { setCurrent(doc); }, [doc]);
  useEffect(() => { getApprovedReferences(doc.id).then(setApproved); }, [doc.id]);
  const active = useActiveSection(current);
  const shareText = () => onShare?.(`${current.title}\n\n${current.in_brief}\n\nSynthesized with Rexxpons RexxSearch`);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.9, ease: "easeOut" }}
      className="relative z-10 min-h-screen w-full px-5 sm:px-8 pt-28 pb-32 max-w-7xl mx-auto"
    >
      <div className="lg:grid lg:grid-cols-12 lg:gap-16">
        <Sidebar active={active} onBack={onBack} />

        <div className="lg:col-span-8 lg:col-start-4">
          <DocHeader title={current.title} onBack={onBack} onShare={onShare ? shareText : undefined} onExport={(lang) => exportDocument(doc, "insight", { lang })} hasUnified={!!doc.unified_output} />

          <div className="flex flex-wrap items-center gap-3 mb-12" data-testid="insight-tools">
            <TranslateControl docId={doc.id} docKind="insight" original={doc} onTranslated={setCurrent} />
            <button
              data-testid="add-source-btn"
              onClick={() => setRefOpen(true)}
              className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-primary transition-colors border border-border hover:border-primary/40 rounded-full px-3 py-1.5"
            >
              <Plus strokeWidth={1.8} className="w-3.5 h-3.5" /> Contribute a source
            </button>
          </div>

          <InBriefSection text={current.in_brief} />
          <div className="h-px bg-border" />

          <section id="analysis" className="scroll-mt-28 py-20">
            <SectionHeading index={2}>The Analysis</SectionHeading>
            <Analysis text={current.analysis} />
          </section>
          <div className="h-px bg-border" />

          <KeyFactsSection facts={current.key_facts} />
          <div className="h-px bg-border" />

          <ConsensusSection consensus={current.consensus} />
          <div className="h-px bg-border" />

          {doc.unified_output && (
            <>
              <UnifiedCognitivePanel unified={doc.unified_output} />
              <div className="h-px bg-border" />
            </>
          )}

          <MediaGallery query={doc.query || doc.title} />
          <div className="h-px bg-border" />

          <SourcesSection sources={current.sources} />
          <CommunityRefs refs={approved} />

          <FollowUpChat docId={current.id} docKind="insight" />
        </div>
      </div>

      <SubmitReferenceModal open={refOpen} onClose={() => setRefOpen(false)} docId={doc.id} docKind="insight" />
    </motion.div>
  );
};
